import React, { useEffect, useMemo, useState } from "react";
import {
  BrowserRouter,
  Link,
  NavLink,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import "./styles/App.css";

const STORAGE_KEY = "vendor-renewal-rescue-studio:full-auto:campaigns";
const DRAFT_KEY = "vendor-renewal-rescue-studio:full-auto:draft";

const sampleSeed = `Figma, $14,400, renews 2026-08-14, Design team owner, high usage but annual auto-renew and seats grew fast.
HubSpot Marketing, $38,000, renews 2026-09-02, Growth owner, valuable but duplicate reporting and renewal quote increased 18%.
Notion, $9,800, renews 2026-07-30, Ops owner, many dormant seats and vendor has startup discount history.
Zoom, $12,600, renews 2026-10-12, Client success owner, must keep but can reduce webinar add-on and unused licenses.`;

const riskWords = ["auto", "increase", "unused", "dormant", "duplicate", "must keep", "renewal quote", "seats"];
const leverageWords = ["discount", "duplicate", "unused", "dormant", "quote", "reduce", "annual", "startup"];

function loadCampaigns() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCampaigns(campaigns) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(campaigns));
}

function money(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.max(0, Math.round(value || 0)));
}

function parseDate(text, offsetDays) {
  const iso = text.match(/\b20\d{2}[-/]\d{1,2}[-/]\d{1,2}\b/);
  if (iso) {
    const normalized = iso[0].replaceAll("/", "-");
    const parsed = new Date(`${normalized}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  const month = text.match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\s+(\d{1,2})\b/i);
  if (month) {
    const date = new Date(`${month[1]} ${month[2]}, 2026`);
    if (!Number.isNaN(date.getTime())) return date;
  }
  const fallback = new Date();
  fallback.setDate(fallback.getDate() + offsetDays);
  return fallback;
}

function daysUntil(date) {
  return Math.max(0, Math.round((date.getTime() - Date.now()) / 86400000));
}

function titleCase(value) {
  return value
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function detectSignals(text, dictionary) {
  const lower = text.toLowerCase();
  return dictionary.filter((word) => lower.includes(word));
}

function parseRenewals(seed) {
  return seed
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const amountMatch = line.match(/\$?\s?([0-9][0-9,]*(?:\.\d{1,2})?)/);
      const amount = amountMatch ? Number(amountMatch[1].replace(/,/g, "")) : 6000 + index * 2500;
      const renewalDate = parseDate(line, 20 + index * 18);
      const vendorName = titleCase(line.split(/[,;-]/)[0] || `Vendor ${index + 1}`);
      const signals = detectSignals(line, riskWords);
      const leverage = detectSignals(line, leverageWords);
      const renewalDays = daysUntil(renewalDate);
      const amountScore = Math.min(32, amount / 1400);
      const timingScore = renewalDays <= 14 ? 32 : renewalDays <= 45 ? 25 : renewalDays <= 90 ? 16 : 9;
      const riskScore = Math.min(24, signals.length * 6);
      const leverageScore = Math.min(18, leverage.length * 5);
      const score = Math.round(amountScore + timingScore + riskScore + leverageScore);
      const savingsRate = 0.08 + Math.min(0.17, (riskScore + leverageScore) / 250);

      return {
        id: `vendor-${index + 1}`,
        vendorName,
        originalLine: line,
        amount,
        renewalDate: renewalDate.toISOString().slice(0, 10),
        renewalDays,
        signals,
        leverage,
        score,
        estimatedSavings: Math.round(amount * savingsRate),
      };
    })
    .sort((a, b) => b.score - a.score);
}

function nextMove(vendor) {
  if (vendor.renewalDays <= 14) return "Open a same-week renewal save conversation";
  if (vendor.signals.includes("unused") || vendor.signals.includes("dormant")) return "Request a seat audit before the quote locks";
  if (vendor.signals.includes("increase") || vendor.signals.includes("renewal quote")) return "Challenge the increase with a lower-risk renewal path";
  return "Ask for a decision-ready renewal option";
}

function makeSequence(vendor, rank) {
  const savings = money(vendor.estimatedSavings);
  return [
    {
      when: rank === 1 ? "Today" : "Within 24 hours",
      subject: `${vendor.vendorName}: renewal decision before ${vendor.renewalDate}`,
      body: `Open with the current ${money(vendor.amount)} renewal, name the risk signals, and ask for a save option worth at least ${savings}.`,
    },
    {
      when: vendor.renewalDays <= 30 ? "Two business days later" : "Three business days later",
      subject: `${vendor.vendorName}: usage and scope check`,
      body: `Ask for a usage export, dormant-seat count, add-on breakdown and a comparison against the current team need.`,
    },
    {
      when: "One week before deadline",
      subject: `${vendor.vendorName}: approve, reduce or replace`,
      body: `Give the vendor three buyer-friendly outcomes: approve the clean renewal, reduce the scope, or keep the door open while alternatives are reviewed.`,
    },
  ];
}

function buildCampaign(seed) {
  const vendors = parseRenewals(seed);
  if (vendors.length < 3) {
    throw new Error("Paste at least three renewal lines so the campaign can rank real tradeoffs.");
  }

  const ranked = vendors.map((vendor, index) => ({
    ...vendor,
    rank: index + 1,
    nextMove: nextMove(vendor),
    sequence: makeSequence(vendor, index + 1),
  }));
  const totalSpend = ranked.reduce((sum, vendor) => sum + vendor.amount, 0);
  const totalSavings = ranked.reduce((sum, vendor) => sum + vendor.estimatedSavings, 0);
  const urgentCount = ranked.filter((vendor) => vendor.renewalDays <= 45).length;
  const id = `renewal-${crypto.randomUUID().slice(0, 8)}`;
  const now = new Date().toISOString();

  return {
    id,
    createdAt: now,
    title: `${ranked[0].vendorName} renewal rescue campaign`,
    status: "Ready to use",
    seed,
    ranked,
    totalSpend,
    totalSavings,
    urgentCount,
    summary: `${ranked.length} renewals reviewed, ${money(totalSpend)} in spend triaged, and ${money(totalSavings)} in directional savings surfaced.`,
  };
}

function Metric({ label, value }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="app-shell">
      <header className="topbar">
        <Link className="brand" to="/">
          <span className="brand-mark">AW</span>
          <span>
            <strong>Vendor Renewal Rescue Studio</strong>
            <small>Operations Finance</small>
          </span>
        </Link>
        <nav aria-label="Main navigation">
          <NavLink to="/">Studio</NavLink>
          <NavLink to="/campaigns">Campaigns</NavLink>
          <NavLink to="/pricing">Pricing</NavLink>
        </nav>
      </header>
      <main>{children}</main>
    </div>
  );
}

function Home() {
  const [seed, setSeed] = useState(() => localStorage.getItem(DRAFT_KEY) || sampleSeed);
  const [campaign, setCampaign] = useState(null);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, seed);
  }, [seed]);

  function startCampaign(event) {
    event.preventDefault();
    setError("");
    setProcessing(true);
    window.setTimeout(() => {
      try {
        const nextCampaign = buildCampaign(seed);
        const campaigns = loadCampaigns().filter((item) => item.id !== nextCampaign.id);
        campaigns.unshift(nextCampaign);
        saveCampaigns(campaigns);
        setCampaign(nextCampaign);
        navigate(`/campaigns/${nextCampaign.id}`);
        window.scrollTo(0, 0);
      } catch (caught) {
        setError(caught.message);
      } finally {
        setProcessing(false);
      }
    }, 500);
  }

  return (
    <Shell>
      <section className="hero">
        <div className="hero-copy">
          <p className="eyebrow">Operations Finance</p>
          <h1>Stop surprise renewals before they swallow margin.</h1>
          <p>
            Paste a messy renewal list once. Get a ranked savings campaign, vendor-specific negotiation scripts,
            deadline timing and a shareable brief your team can act on immediately.
          </p>
          <div className="hero-actions">
            <a className="button primary" href="#renewal-input">Start renewal rescue</a>
            <Link className="button ghost hero-ghost" to="/campaigns">View saved campaigns</Link>
          </div>
        </div>
        <aside className="hero-card">
          <p className="eyebrow">First useful result</p>
          <strong>Rank the renewal saves that deserve attention this week.</strong>
          <p>Built for owner-led teams that have renewals scattered across emails, spreadsheets and memory.</p>
        </aside>
      </section>

      <section className="panel input-panel" id="renewal-input">
        <div className="section-header">
          <div>
            <p className="eyebrow">Minimal seed</p>
            <h2>Paste renewal notes</h2>
          </div>
          <button type="button" className="ghost" onClick={() => setSeed(sampleSeed)}>Use example</button>
        </div>
        <form onSubmit={startCampaign}>
          <label htmlFor="renewal-seed">
            Renewal list
            <textarea
              id="renewal-seed"
              value={seed}
              onChange={(event) => {
                setSeed(event.target.value);
                if (error) setError("");
              }}
              rows="9"
              placeholder="Vendor, annual cost, renewal date, owner, usage or negotiation notes..."
              required
            />
          </label>
          <div className="action-row">
            <button className="primary" type="submit" disabled={processing} data-testid="start-campaign">
              {processing ? "Building renewal campaign..." : "Build renewal rescue campaign"}
            </button>
            <span className={error ? "status error" : "status"}>
              {error || "One pasted list and one click completes the plan."}
            </span>
          </div>
        </form>
      </section>

      <section className="proof-grid">
        <article>
          <span>1</span>
          <h3>Immediate structure</h3>
          <p>Vendor names, spend, deadlines and risk language are extracted from the pasted list.</p>
        </article>
        <article>
          <span>2</span>
          <h3>Prioritized savings</h3>
          <p>The plan ranks renewals by spend, deadline pressure and negotiation leverage.</p>
        </article>
        <article>
          <span>3</span>
          <h3>Action-ready output</h3>
          <p>Each vendor receives a three-step outreach plan and a shareable executive brief.</p>
        </article>
      </section>

      {campaign && <CampaignSummary campaign={campaign} compact />}
    </Shell>
  );
}

function CampaignSummary({ campaign, compact = false }) {
  return (
    <section className={compact ? "panel compact-result" : "panel"}>
      <div className="result-hero">
        <div>
          <p className="eyebrow">Renewal campaign</p>
          <h1>{campaign.title}</h1>
          <p>{campaign.summary}</p>
        </div>
        <Link className="button secondary" to={`/brief/${campaign.id}`}>Open shareable brief</Link>
      </div>
      <div className="metric-grid">
        <Metric label="Campaign ID" value={campaign.id} />
        <Metric label="Spend reviewed" value={money(campaign.totalSpend)} />
        <Metric label="Savings surfaced" value={money(campaign.totalSavings)} />
        <Metric label="Near deadlines" value={`${campaign.urgentCount} renewals`} />
      </div>
      {!compact && (
        <div className="result-grid">
          <RankedRenewals vendors={campaign.ranked} />
          <OutreachPlan vendors={campaign.ranked} />
        </div>
      )}
    </section>
  );
}

function RankedRenewals({ vendors }) {
  return (
    <section className="panel nested">
      <p className="eyebrow">Priority order</p>
      <h2>Follow this renewal order</h2>
      <div className="card-stack">
        {vendors.map((vendor) => (
          <article className="vendor-card" key={vendor.id}>
            <div className="rank-row">
              <span className="rank-pill">#{vendor.rank}</span>
              <strong>{vendor.score}/100</strong>
            </div>
            <h3>{vendor.vendorName}</h3>
            <p>{money(vendor.amount)} renewal on {vendor.renewalDate}</p>
            <p className="muted">{vendor.nextMove}</p>
            <p className="savings">{money(vendor.estimatedSavings)} directional savings</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function OutreachPlan({ vendors }) {
  return (
    <section className="panel nested">
      <p className="eyebrow">Vendor outreach</p>
      <h2>Three-step negotiation plans</h2>
      <div className="card-stack">
        {vendors.map((vendor) => (
          <article className="sequence-card" key={vendor.id}>
            <h3>{vendor.vendorName}</h3>
            <p className="next-move">{vendor.nextMove}</p>
            <ol>
              {vendor.sequence.map((step) => (
                <li key={step.subject}>
                  <strong>{step.when} - {step.subject}</strong>
                  <p>{step.body}</p>
                </li>
              ))}
            </ol>
          </article>
        ))}
      </div>
    </section>
  );
}

function CampaignDetail() {
  const { campaignId } = useParams();
  const campaign = useMemo(() => loadCampaigns().find((item) => item.id === campaignId), [campaignId]);

  if (!campaign) {
    return (
      <Shell>
        <section className="panel empty-state">
          <p className="eyebrow">Saved campaign</p>
          <h1>Campaign not found in this browser.</h1>
          <p className="muted">Create a renewal campaign here first, then reopen it from campaign history.</p>
          <Link className="button primary" to="/">Start with a renewal list</Link>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      <CampaignSummary campaign={campaign} />
      <section className="panel">
        <p className="eyebrow">Deadline plan</p>
        <h2>Recommended timing</h2>
        <div className="timeline">
          {campaign.ranked.map((vendor) => (
            <article key={vendor.id}>
              <strong>{vendor.vendorName}: {vendor.nextMove}</strong>
              <p>{vendor.renewalDays} days until renewal. Protect {money(vendor.estimatedSavings)} before the window closes.</p>
            </article>
          ))}
        </div>
      </section>
    </Shell>
  );
}

function Campaigns() {
  const campaigns = loadCampaigns();
  return (
    <Shell>
      <section className="panel">
        <div className="section-header">
          <div>
            <p className="eyebrow">Campaign history</p>
            <h1>Saved renewal campaigns</h1>
          </div>
          <Link className="button primary" to="/">New campaign</Link>
        </div>
        {campaigns.length === 0 ? (
          <div className="empty-state">
            <h2>No renewal campaigns yet.</h2>
            <p className="muted">Paste at least three renewal lines to create the first saved plan.</p>
          </div>
        ) : (
          <div className="history-list">
            {campaigns.map((campaign) => (
              <Link className="history-row" to={`/campaigns/${campaign.id}`} key={campaign.id}>
                <span>
                  <strong>{campaign.title}</strong>
                  <small>{new Date(campaign.createdAt).toLocaleString()} - {campaign.status}</small>
                </span>
                <b>{money(campaign.totalSavings)} surfaced</b>
              </Link>
            ))}
          </div>
        )}
      </section>
    </Shell>
  );
}

function Brief() {
  const { campaignId } = useParams();
  const campaign = useMemo(() => loadCampaigns().find((item) => item.id === campaignId), [campaignId]);

  if (!campaign) {
    return (
      <Shell>
        <section className="panel empty-state">
          <h1>Brief unavailable</h1>
          <p className="muted">This browser does not have the saved campaign yet.</p>
          <Link className="button primary" to="/">Create a campaign</Link>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="brief-hero">
        <p className="eyebrow">Executive renewal brief</p>
        <h1>{campaign.summary}</h1>
        <p>Start with {campaign.ranked[0].vendorName}: {campaign.ranked[0].nextMove.toLowerCase()}.</p>
      </section>
      <section className="panel">
        <h2>Ready-to-use renewal packet</h2>
        <div className="brief-grid">
          {campaign.ranked.slice(0, 3).map((vendor) => (
            <article className="vendor-card" key={vendor.id}>
              <p className="eyebrow">Priority {vendor.rank}</p>
              <h3>{vendor.vendorName}</h3>
              <p>{money(vendor.amount)} renewal - {money(vendor.estimatedSavings)} savings target</p>
              <p className="next-move">{vendor.sequence[0].subject}</p>
            </article>
          ))}
        </div>
      </section>
    </Shell>
  );
}

function Pricing() {
  return (
    <Shell>
      <section className="pricing-panel">
        <p className="eyebrow">Simple commercial plan</p>
        <h1>$79 per renewal rescue campaign.</h1>
        <p>
          Build the renewal packet first, inspect the ranked savings plan, then use the brief with your owner,
          finance or vendor-management team. No card is required for this benchmark deployment.
        </p>
        <Link className="button secondary" to="/">Start with renewals</Link>
      </section>
    </Shell>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/campaigns" element={<Campaigns />} />
        <Route path="/campaigns/:campaignId" element={<CampaignDetail />} />
        <Route path="/brief/:campaignId" element={<Brief />} />
        <Route path="/pricing" element={<Pricing />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
